/**
 * V-Mega Menu Component
 * 4-level hover-activated navigation menu with featured items
 * Uses pre-rendered HTML with show/hide logic (no DOM manipulation)
 */

// Dwell time before a hovered entry swaps the next column. Prevents
// accidental swaps when the cursor crosses sibling rows on its way to a submenu.
const SWITCH_DELAY = 200;
// Grace period before the whole menu closes after the pointer leaves it.
const CLOSE_DELAY = 300;
// Delay before opening the dropdown the first time (avoids accidental opens
// when the cursor crosses the black bar on the way down the page).
const OPEN_DELAY = 220;
// Delay before switching to another level 1 item when the dropdown is already open.
const BAR_SWITCH_DELAY = 200;

class VMenu {
  constructor(rootEl) {
    this.rootEl = rootEl;
    this.barEl = rootEl?.querySelector(".v-mega-menu__bar .v-mega-menu__level--root");
    this.dropdownEl = rootEl?.querySelector(".v-mega-menu__dropdown");

    if (!this.barEl || !this.dropdownEl) return;

    // Debug flag - set to true to keep dropdown open for styling/debugging
    this.debugMode = false;

    // Track current open level 1 item
    this.currentLevel1 = null;

    // Track committed (visible) level 2 / level 3 / level 4 selections
    this.activeLevel2 = null;
    this.activeLevel3 = null;
    this.activeLevel4 = null;

    // Hover-intent state
    this._switchTimer = null;
    this._pendingSwitch = null;
    this._openTimer = null;
    this._closeTimer = null;
    this._lastPointerX = null;

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.closeMenu = this.closeMenu.bind(this);

    this.init();
  }

  /**
   * Schedules a column swap after a dwell delay. A delay of 0 commits immediately.
   */
  _scheduleSwitch(level, id, delay = SWITCH_DELAY) {
    this._cancelSwitch();

    if (delay === 0) {
      this.updateColumns(level, id);
      return;
    }

    this._pendingSwitch = { level, id, delay };
    this._switchTimer = setTimeout(() => {
      this._switchTimer = null;
      this._pendingSwitch = null;
      this.updateColumns(level, id);
    }, delay);
  }

  _cancelSwitch() {
    clearTimeout(this._switchTimer);
    this._switchTimer = null;
    this._pendingSwitch = null;
  }

  _cancelClose() {
    clearTimeout(this._closeTimer);
    this._closeTimer = null;
  }

  /**
   * Closes the dropdown
   */
  closeMenu() {
    this._cancelSwitch();
    this._cancelClose();
    clearTimeout(this._openTimer);
    this._lastPointerX = null;

    // Hide all level 1 content groups
    this.dropdownEl.querySelectorAll('[data-level1]').forEach(el => el.hidden = true);
    this.dropdownEl.classList.remove("v-mega-menu__dropdown--open");
    this.currentLevel1 = null;
    this.activeLevel2 = null;
    this.activeLevel3 = null;
    this.activeLevel4 = null;

    // Reset aria-expanded on all level 1 links
    this.barEl.querySelectorAll('[data-level="1"]').forEach(link => {
      link.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Initializes event listeners
   */
  init() {
    this.attachGlobalEvents();
    this.attachBarEvents();
    this.attachDropdownEvents();
  }

  /**
   * Attaches events to level 1 nav bar
   */
  attachBarEvents() {
    // Hover to open dropdown
    this.barEl.querySelectorAll('[data-level="1"]').forEach(link => {
      link.addEventListener('mouseenter', () => {
        this._cancelClose();
        clearTimeout(this._openTimer);

        const level1Id = link.dataset.id;
        if (this.currentLevel1 === level1Id) return;

        // Items without dropdown content (no children, no featured) never open
        // a dropdown; hovering them closes whatever is currently open instead.
        if (!this.dropdownEl.querySelector(`[data-level1="${CSS.escape(level1Id)}"]`)) {
          if (this.currentLevel1) {
            this._openTimer = setTimeout(this.closeMenu, BAR_SWITCH_DELAY);
          }
          return;
        }

        if (!this.currentLevel1) {
          // Nothing open yet: require a short dwell so crossing the bar
          // en route down the page doesn't open the menu by accident
          this._openTimer = setTimeout(() => this.openDropdown(level1Id), OPEN_DELAY);
        } else {
          // Another item is open: require a short dwell before switching,
          // so grazing a neighbouring item doesn't wipe the current state
          this._openTimer = setTimeout(() => this.openDropdown(level1Id), BAR_SWITCH_DELAY);
        }
      });

      link.addEventListener('mouseleave', () => clearTimeout(this._openTimer));

      link.addEventListener('click', (e) => {
        // Only prevent navigation if there's no URL (href="#")
        if (link.getAttribute('href') === '#') {
          e.preventDefault();
        }
      });
    });
  }

  /**
   * Attaches hover events within dropdown
   */
  attachDropdownEvents() {
    // While the pointer travels rightwards (towards the submenu column),
    // keep postponing any pending swap so a diagonal path over sibling
    // rows doesn't replace the submenu the user is aiming for.
    this.dropdownEl.addEventListener('mousemove', (e) => {
      if (this._lastPointerX !== null && this._pendingSwitch) {
        const dx = e.clientX - this._lastPointerX;
        if (dx > 2) {
          const { level, id, delay } = this._pendingSwitch;
          this._scheduleSwitch(level, id, delay);
        }
      }
      this._lastPointerX = e.clientX;
    });

    // Handle hover on level 2/3 (next column) and level 4 (featured tile)
    this.dropdownEl.addEventListener('mouseenter', (e) => {
      const entry = e.target.closest('[data-id][data-level]');
      if (!entry) return;

      const level = parseInt(entry.dataset.level);
      const id = entry.dataset.id;

      if (level !== 2 && level !== 3 && level !== 4) return;

      const activeId = level === 2
        ? this.activeLevel2
        : level === 3
          ? this.activeLevel3
          : this.activeLevel4;

      if (activeId === id) {
        // Re-entered the already committed entry: keep current state
        this._cancelSwitch();
        return;
      }

      // First selection on this level commits immediately; switching away
      // from an existing selection requires a short dwell
      const delay = activeId === null ? 0 : SWITCH_DELAY;
      this._scheduleSwitch(level, id, delay);
    }, true);

    // Crossing out of an entry before the dwell elapses cancels the swap
    this.dropdownEl.addEventListener('mouseleave', (e) => {
      if (!this._pendingSwitch) return;
      const entry = e.target.closest?.('[data-id][data-level]');
      if (entry && entry.dataset.id === this._pendingSwitch.id) {
        this._cancelSwitch();
      }
    }, true);

    // Prevent navigation only on items without URLs (href="#")
    this.dropdownEl.addEventListener('click', (e) => {
      const link = e.target.closest('a.v-mega-menu__entry-link');
      if (link && link.getAttribute('href') === '#') {
        e.preventDefault();
      }
    }, true);
  }

  /**
   * Attaches global events (close on outside click, keyboard, etc.)
   */
  attachGlobalEvents() {
    // Close on Escape
    document.addEventListener('keydown', this.handleKeyDown);

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (this.debugMode) return;
      if (!this.rootEl.contains(e.target)) {
        this.closeMenu();
      }
    });

    // Close when mouse leaves entire component (with a grace period)
    this.rootEl.addEventListener('mouseleave', this.handleMouseLeave);

    // Re-entering within the grace period keeps the menu open
    this.rootEl.addEventListener('mouseenter', () => this._cancelClose());
  }

  /**
   * Opens dropdown for a specific level 1 item
   */
  openDropdown(level1Id) {
    this._cancelSwitch();

    // Hide all level 1 content groups
    this.dropdownEl.querySelectorAll('[data-level1]').forEach(el => el.hidden = true);

    // Show the selected level 1 content
    const content = this.dropdownEl.querySelector(`[data-level1="${level1Id}"]`);
    if (!content) return;

    content.hidden = false;
    this.dropdownEl.classList.add('v-mega-menu__dropdown--open');
    this.currentLevel1 = level1Id;

    // Update aria-expanded
    this.barEl.querySelectorAll('[data-level="1"]').forEach(link => {
      const isOpen = link.dataset.id === level1Id;
      link.setAttribute('aria-expanded', isOpen);
    });

    // Reset to level 1 state (show level 1 featured, hide sub-columns)
    this.resetToLevel1(content);
  }

  /**
   * Resets dropdown to show only level 1 featured items
   */
  resetToLevel1(content) {
    this.activeLevel2 = null;
    this.activeLevel3 = null;
    this.activeLevel4 = null;

    // Hide all column 3 and 4 groups
    content.querySelectorAll('[data-level="3"] [data-parent]').forEach(el => el.hidden = true);
    content.querySelectorAll('[data-level="4"] [data-parent]').forEach(el => el.hidden = true);

    // Hide all featured tiles
    content.querySelectorAll('.v-mega-menu__tile[data-for]').forEach(el => el.hidden = true);

    // Show level 1 featured tiles (can be 1 or 2 tiles)
    const level1Tiles = content.querySelectorAll(`.v-mega-menu__tile[data-for="${this.currentLevel1}"]`);
    level1Tiles.forEach(tile => tile.hidden = false);

    // Set layout classes for level 1 (double featured, 1/3 menu)
    const featuredArea = content.querySelector('.v-mega-menu__featured');
    const contentArea = content;

    featuredArea.classList.remove('v-mega-menu__featured--single', 'v-mega-menu__featured--double');
    contentArea.classList.remove('v-mega-menu__content--single', 'v-mega-menu__content--double', 'v-mega-menu__content--has-level4');

    // Don't add any classes for level 1 - let base CSS handle it (1fr 2fr grid)
  }

  /**
   * Updates columns 3 and 4 based on hover
   */
  updateColumns(level, id) {
    const content = this.dropdownEl.querySelector(`[data-level1="${this.currentLevel1}"]`);
    if (!content) return;

    if (level === 2) {
      this.activeLevel2 = id;
      this.activeLevel3 = null;
      this.activeLevel4 = null;

      // Hovering on level 2 - show corresponding level 3 group, hide level 4
      const col3 = content.querySelector('[data-level="3"]');
      const col4 = content.querySelector('[data-level="4"]');

      // Hide all level 3 groups
      col3.querySelectorAll('[data-parent]').forEach(el => el.hidden = true);

      // Show the level 3 group for this level 2 item
      const level3Group = col3.querySelector(`[data-parent="${id}"]`);
      if (level3Group) {
        level3Group.hidden = false;
      }

      // Hide all level 4 groups
      col4.querySelectorAll('[data-parent]').forEach(el => el.hidden = true);

      // Update featured area for level 2
      this.updateFeatured(id, level);

      // Update layout classes (single featured, 2/3 menu)
      const featuredArea = content.querySelector('.v-mega-menu__featured');
      featuredArea.classList.remove('v-mega-menu__featured--double');
      featuredArea.classList.add('v-mega-menu__featured--single');
      content.classList.remove('v-mega-menu__content--double');
      content.classList.add('v-mega-menu__content--single');

      // Check if level 4 exists
      const hasLevel4 = level3Group && level3Group.querySelector('[data-has-children]');
      content.classList.toggle('v-mega-menu__content--has-level4', !!hasLevel4);

    } else if (level === 3) {
      this.activeLevel3 = id;
      this.activeLevel4 = null;

      // Hovering on level 3 - show corresponding level 4 group
      const col4 = content.querySelector('[data-level="4"]');

      // Hide all level 4 groups
      col4.querySelectorAll('[data-parent]').forEach(el => el.hidden = true);

      // Show the level 4 group for this level 3 item
      const level4Group = col4.querySelector(`[data-parent="${id}"]`);
      if (level4Group) {
        level4Group.hidden = false;
        content.classList.add('v-mega-menu__content--has-level4');
      } else {
        content.classList.remove('v-mega-menu__content--has-level4');
      }

      // Update featured area for level 3
      this.updateFeatured(id, level);

      // Layout classes remain "single" (already set from level 2)
    } else if (level === 4) {
      // Hovering on level 4 — swap featured tile only (no deeper column)
      this.activeLevel4 = id;
      this.updateFeatured(id, level);
    }
  }

  /**
   * Updates featured area based on active item
   */
  updateFeatured(id, level) {
    const content = this.dropdownEl.querySelector(`[data-level1="${this.currentLevel1}"]`);
    if (!content) return;

    const featuredArea = content.querySelector('.v-mega-menu__featured');

    // Hide all featured tiles
    content.querySelectorAll('.v-mega-menu__tile[data-for]').forEach(el => el.hidden = true);

    // Show featured for current item (with fallback to parent levels)
    let featuredTiles = featuredArea.querySelectorAll(`.v-mega-menu__tile[data-for="${id}"]`);

    if (featuredTiles.length === 0 && level === 4) {
      // Level 4 has no featured — try level 3 parent, then level 2
      const level4Entry = content.querySelector(`[data-id="${id}"]`);
      const level3Id = level4Entry?.closest('[data-parent]')?.dataset.parent;
      if (level3Id) {
        featuredTiles = featuredArea.querySelectorAll(`.v-mega-menu__tile[data-for="${level3Id}"]`);
        if (featuredTiles.length === 0) {
          const level3Entry = content.querySelector(`[data-id="${level3Id}"]`);
          const level2Id = level3Entry?.closest('[data-parent]')?.dataset.parent;
          if (level2Id) {
            featuredTiles = featuredArea.querySelectorAll(`.v-mega-menu__tile[data-for="${level2Id}"]`);
          }
        }
      }
    }

    if (featuredTiles.length === 0 && level === 3) {
      // Level 3 has no featured - try level 2 parent
      const level3Entry = content.querySelector(`[data-id="${id}"]`);
      const level2Group = level3Entry?.closest('[data-parent]');
      const level2Id = level2Group?.dataset.parent;
      if (level2Id) {
        featuredTiles = featuredArea.querySelectorAll(`.v-mega-menu__tile[data-for="${level2Id}"]`);
      }
    }

    if (featuredTiles.length === 0) {
      // Still no featured - fallback to level 1
      featuredTiles = featuredArea.querySelectorAll(`.v-mega-menu__tile[data-for="${this.currentLevel1}"]`);
    }

    // Show the featured tile(s)
    featuredTiles.forEach(tile => tile.hidden = false);
  }

  /**
   * Mouse leave handler
   */
  handleMouseLeave() {
    if (this.debugMode) return;
    this._cancelSwitch();
    clearTimeout(this._openTimer);
    this._cancelClose();
    this._closeTimer = setTimeout(this.closeMenu, CLOSE_DELAY);
  }

  /**
   * Keyboard handler
   */
  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.closeMenu();
    }
  }

  /**
   * Mouse enter handler (required for binding)
   */
  handleMouseEnter() {
    // Implemented via direct event listeners in attachBarEvents
  }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('[data-component="v-mega-menu"]');
  if (el) new VMenu(el);
});
